import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaUser } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
// Use public path directly in src attribute, not import

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();

  // Handle submenu toggles
  const toggleSubmenu = useCallback((menu) => {
    setOpenSubmenu(prevState => prevState === menu ? null : menu);
  }, []);

  // Reset open submenus and mobile menu when location changes
  useEffect(() => {
    setOpenSubmenu(null);
    setIsOpen(false);
    // Scroll to top with a small delay to ensure page is loaded
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 100);
  }, [location.pathname]);

  // Close submenus and mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-item-dropdown')) {
        setOpenSubmenu(null);
      }
      if (!event.target.closest('.mobile-menu-container') && !event.target.closest('.mobile-menu-button')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Centralized navigation function to handle scroll and menu closing
  const handleNavigation = () => {
    setIsOpen(false);
    setOpenSubmenu(null);
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 50);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);





  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? `bg-white shadow-md py-2` 
          : `bg-white py-4`
      }`}
    >
      <div className="section-container flex justify-between items-center">
        <Link to="/" className="flex items-center" onClick={handleNavigation}>
          <img
            src="https://res.cloudinary.com/dqwxmksbe/image/upload/v1762423871/blockfirstcapital-dark-logo_dcbbka.png"
            alt="Blockfirst Capital"
            className="h-10 lg:h-14 w-auto"
            style={{ 
              objectFit: 'contain',
              maxWidth: '120px',
              maxHeight: '56px'
            }}
            loading="eager"
          />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-2">
          <nav className="flex items-center space-x-3">
            <NavLink 
              to="/about" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              About Us
            </NavLink>
            

            
            <NavLink 
              to="/contact" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              Contact Us
            </NavLink>
            
            <NavLink 
              to="/futures-trading" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              Futures Trading
            </NavLink>

            <NavLink 
              to="/earn-crypto" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              Earn Crypto
            </NavLink>

            <NavLink 
              to="/tax-reporting" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              Tax Reporting
            </NavLink>

            <NavLink 
              to="/web3" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              Web3
            </NavLink>
            
            <NavLink 
              to="/buy-crypto" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              Buy Crypto
            </NavLink>
            
            <NavLink 
              to="/exchange" 
              className={({ isActive }) => isActive 
                ? "text-amber-600 font-semibold py-2 text-sm" 
                : `text-gray-700 hover:text-amber-600 transition-colors px-2 py-2 font-medium text-sm`}
              onClick={handleNavigation}
            >
              Exchange
            </NavLink>
            
            <a 
              href="https://web3.blockfirstcapital.com/" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-900 text-white hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors font-medium text-sm"
            >
              Login
            </a>
            
          </nav>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center space-x-4">
          {/* Mobile Menu Toggle */}
          <button 
            onClick={toggleMenu} 
            className="mobile-menu-button text-gray-900 focus:outline-none ml-2"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop overlay */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Mobile Menu */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="mobile-menu-container md:hidden bg-white rounded-t-3xl shadow-2xl relative z-50 max-h-[90vh] overflow-y-auto"
            >
              {/* Header with Logo and Close */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <img src="https://res.cloudinary.com/dqwxmksbe/image/upload/v1762423871/blockfirstcapital-dark-logo_dcbbka.png" alt="Blockfirst Capital" className="h-8" />
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-gray-900 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>

              {/* Navigation Items */}
              <nav className="px-6 py-4">
                {/* Main Navigation */}
                <div className="space-y-0">
                  <Link
                    to="/about"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    About Us
                  </Link>
                  

                  
                  <Link
                    to="/contact"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    Contact Us
                  </Link>
                  
                  <Link
                    to="/futures-trading"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    Futures Trading
                  </Link>

                  <Link
                    to="/earn-crypto"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    Earn Crypto
                  </Link>

                  <Link
                    to="/tax-reporting"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    Tax Reporting
                  </Link>

                  <Link
                    to="/web3"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    Web3
                  </Link>
                  
                  <Link
                    to="/buy-crypto"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    Buy Crypto
                  </Link>
                  
                  <Link
                    to="/exchange"
                    className="block py-4 text-gray-900 hover:text-gray-600 transition-colors border-b border-gray-100"
                    onClick={handleNavigation}
                  >
                    Exchange
                  </Link>
                </div>

                {/* Action Button */}
                <div className="mt-8">
                  <a
                    href="https://web3.blockfirstcapital.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 px-4 text-center bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Login
                  </a>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;